import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import authService from '../Services/AuthService';
import { gravatarUrl } from '../Utils/gravatar';

interface AuthUser {
  displayName?: string;
  email?: string;
  roles?: string[];
  photoUrl?: string;
}

interface AuthContextValue {
  user: AuthUser;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  requireAccessToken: (opts?: { allowPopup?: boolean }) => Promise<string | null>;
  isUserAuthenticated: () => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthenticationProvider');
  return ctx;
}

export function AuthenticationProvider(props: { children: React.ReactNode }) {
  const [authentication, setAuthentication] = useState<{
    error: string | null;
    isAuthenticated: boolean;
    user: AuthUser;
  }>({
    error: null,
    isAuthenticated: false,
    user: {
      displayName: '',
      email: '',
      roles: [],
    },
  });

  useEffect(() => {
    async function checkIfUserIsAuthenticated() {
      const account = await authService.isUserAuthenticated();
      const isAuthenticated = Object.keys(account).length !== 0;
      setAuthentication({
        isAuthenticated: isAuthenticated,
        user: {
          displayName: account?.displayName,
          email: account?.email,
          roles: account?.roles,
        },
        error: null,
      });
      if (!isAuthenticated) {
        authService.clearAllCookies();
      }
    }
    checkIfUserIsAuthenticated();
  }, []);

  const resolvedEmailRef = useRef<string | null>(null);

  useEffect(() => {
    const email = authentication.user.email;
    if (!email) {
      // Logged out: allow re-resolution if the same user signs back in.
      resolvedEmailRef.current = null;
      return;
    }
    if (resolvedEmailRef.current === email) return;
    resolvedEmailRef.current = email;

    let cancelled = false;
    (async () => {
      let photoUrl: string | undefined;
      try {
        photoUrl = await gravatarUrl(email);
      } catch {
        photoUrl = undefined;
      }
      if (cancelled) return;
      setAuthentication(prev => ({
        ...prev,
        user: { ...prev.user, photoUrl },
      }));
    })();

    return () => {
      cancelled = true;
    };
  }, [authentication.user.email]);

  const login = async () => {
    const account = await authService.login();
    setAuthentication({
      isAuthenticated: Object.keys(account).length !== 0,
      user: {
        displayName: account.displayName,
        email: account.email,
        roles: account.roles,
      },
      error: null,
    });
  };

  const logout = async () => {
    await authService.logout();
    setAuthentication({
      isAuthenticated: false,
      user: {},
      error: null,
    });
  };

  const isUserAuthenticated = () => {
    return authentication.isAuthenticated;
  };

  const requireAccessToken = async (opts?: { allowPopup?: boolean }) => {
    return await authService.requireAccessToken(opts);
  };

  const { user } = authentication;

  const value: AuthContextValue = {
    user,
    login,
    logout,
    requireAccessToken,
    isUserAuthenticated,
  };

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}
